import { useState, useRef, useEffect } from 'react';

const color = '#ff2d78';

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  tag: string;
  tagColor: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'q1',
    question: 'Можно ли принимать NeuroPlex Ultra без назначения врача?',
    answer: 'NeuroPlex Ultra является безрецептурным препаратом и доступен для самостоятельного приобретения. Однако перед началом курса рекомендуется проконсультироваться с врачом или фармацевтом, особенно если у вас есть хронические заболевания или вы принимаете другие лекарственные средства. Самолечение может быть опасным.',
    tag: 'Применение',
    tagColor: '#ff2d78',
  },
  {
    id: 'q2',
    question: 'Через сколько дней появится эффект от приёма?',
    answer: 'Первые результаты большинство пациентов отмечают уже через 7–14 дней регулярного приёма: улучшается концентрация внимания, снижается умственная усталость. Выраженный нейропротекторный эффект развивается к концу 3–4 недели курса. Для стойкого результата рекомендуется полный курс 30–60 дней.',
    tag: 'Эффективность',
    tagColor: '#39ff14',
  },
  {
    id: 'q3',
    question: 'Как правильно принимать препарат — до или после еды?',
    answer: 'Рекомендуется принимать NeuroPlex Ultra во время или сразу после еды. Это улучшает всасывание активных компонентов и снижает вероятность дискомфорта в желудке. Запивайте достаточным количеством воды (не менее 200 мл). Не рекомендуется принимать натощак.',
    tag: 'Дозировка',
    tagColor: '#00f5ff',
  },
  {
    id: 'q4',
    question: 'Совместим ли NeuroPlex Ultra с алкоголем?',
    answer: 'Совместное употребление с алкоголем не рекомендуется. Этанол снижает эффективность нейропротекторных компонентов и может усиливать побочные эффекты. При необходимости разового употребления алкоголя рекомендуется пропустить приём препарата в этот день.',
    tag: 'Совместимость',
    tagColor: '#f0ff00',
  },
  {
    id: 'q5',
    question: 'Можно ли принимать детям и подросткам?',
    answer: 'Препарат разрешён детям с 5 лет в сниженной дозировке — 1/2 таблетки 1 раз в сутки во время еды. Детям до 5 лет применение противопоказано. Для подростков 12–18 лет дозировка соответствует взрослой. Курс и длительность для детей определяет педиатр или невролог.',
    tag: 'Дети',
    tagColor: '#39ff14',
  },
  {
    id: 'q6',
    question: 'Что делать при пропуске приёма?',
    answer: 'Если вы пропустили приём, примите препарат как можно скорее, но только если до следующего приёма осталось более 6 часов. Если времени меньше — пропустите пропущенную дозу и продолжайте по обычному расписанию. Не удваивайте дозу для компенсации пропущенного приёма.',
    tag: 'Дозировка',
    tagColor: '#00f5ff',
  },
  {
    id: 'q7',
    question: 'Есть ли синдром отмены при прекращении курса?',
    answer: 'NeuroPlex Ultra не вызывает физической зависимости и синдрома отмены. Курс можно прекратить в любой момент без постепенного снижения дозы. Некоторые пациенты отмечают субъективное ощущение снижения концентрации после окончания курса — это нормально и проходит самостоятельно.',
    tag: 'Безопасность',
    tagColor: '#ff6b00',
  },
  {
    id: 'q8',
    question: 'Как хранить препарат после вскрытия упаковки?',
    answer: 'После вскрытия блистера храните таблетки в оригинальной упаковке при температуре +2°C...+8°C, защитив от прямого света и влаги. Не замораживайте. Срок годности после вскрытия не изменяется — ориентируйтесь на дату на упаковке. Не используйте препарат с истёкшим сроком годности.',
    tag: 'Хранение',
    tagColor: '#00f5ff',
  },
];

/* ── Animated accordion item ── */
const FaqAccordionItem = ({
  item,
  isOpen,
  onToggle,
  index,
}: {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (bodyRef.current) {
      setHeight(isOpen ? bodyRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        border: isOpen ? `1px solid ${color}45` : '1px solid rgba(255,255,255,0.06)',
        background: isOpen ? `${color}05` : 'rgba(255,255,255,0.015)',
        boxShadow: isOpen ? `0 0 20px ${color}10` : 'none',
      }}
    >
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-4 px-6 py-5 cursor-pointer text-left group"
      >
        {/* Number */}
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center font-orbitron font-black text-xs shrink-0 mt-0.5 transition-all duration-300"
          style={{
            background: isOpen ? color : 'rgba(255,255,255,0.05)',
            color: isOpen ? '#050508' : 'rgba(255,255,255,0.3)',
            boxShadow: isOpen ? `0 0 10px ${color}60` : 'none',
          }}
        >
          {String(index + 1).padStart(2, '0')}
        </div>

        {/* Question + tag */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 flex-wrap">
            <span
              className="font-mono text-[10px] px-2 py-0.5 rounded-full shrink-0 mt-0.5"
              style={{
                background: `${item.tagColor}12`,
                color: item.tagColor,
                border: `1px solid ${item.tagColor}30`,
              }}
            >
              {item.tag}
            </span>
          </div>
          <p
            className="font-orbitron font-bold text-sm leading-snug mt-1.5 transition-colors duration-300"
            style={{ color: isOpen ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)' }}
          >
            {item.question}
          </p>
        </div>

        {/* Arrow */}
        <div
          className="w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-all duration-300"
          style={{
            background: isOpen ? `${color}18` : 'rgba(255,255,255,0.04)',
            border: isOpen ? `1px solid ${color}40` : '1px solid rgba(255,255,255,0.08)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <i
            className="ri-arrow-down-s-line text-base"
            style={{ color: isOpen ? color : 'rgba(255,255,255,0.35)' }}
          />
        </div>
      </button>

      {/* Body — animated height */}
      <div
        style={{
          height: `${height}px`,
          overflow: 'hidden',
          transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div ref={bodyRef}>
          <div
            className="px-6 pb-6 pt-0"
            style={{ borderTop: `1px solid ${color}15` }}
          >
            {/* Neon left bar */}
            <div className="flex gap-4 pt-4">
              <div
                className="w-0.5 rounded-full shrink-0 self-stretch"
                style={{ background: `linear-gradient(to bottom, ${color}, transparent)`, minHeight: '40px' }}
              />
              <p className="font-mono text-sm text-white/60 leading-relaxed">
                {item.answer}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main FAQ Section ── */
const ProductFaq = () => {
  const [openId, setOpenId] = useState<string | null>('q1');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const allTags = Array.from(new Set(FAQ_ITEMS.map(f => f.tag)));

  const filtered = FAQ_ITEMS.filter(item => {
    const matchesSearch =
      searchQuery.trim() === '' ||
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = activeTag === null || item.tag === activeTag;
    return matchesSearch && matchesTag;
  });

  const handleToggle = (id: string) => {
    setOpenId(prev => (prev === id ? null : id));
  };

  return (
    <section className="py-16 px-4 md:px-8 lg:px-12" style={{ background: '#050508' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            {/* Label */}
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-1 h-6 rounded-full"
                style={{ background: `linear-gradient(to bottom, ${color}, transparent)` }}
              />
              <span
                className="font-mono text-xs tracking-widest uppercase"
                style={{ color: `${color}cc` }}
              >
                Частые вопросы
              </span>
            </div>
            <h2
              className="font-orbitron font-black text-2xl md:text-3xl text-white"
              style={{ textShadow: `0 0 20px ${color}25` }}
            >
              Вопросы и ответы
            </h2>
            <p className="font-mono text-sm text-white/35 mt-2">
              {FAQ_ITEMS.length} вопросов о препарате NeuroPlex Ultra
            </p>
          </div>

          {/* Search */}
          <div
            className="relative w-full md:w-72"
            style={{ flexShrink: 0 }}
          >
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-sm" style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Поиск по вопросам..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl font-mono text-sm text-white bg-transparent outline-none"
              style={{
                border: `1px solid ${searchQuery ? `${color}50` : 'rgba(255,255,255,0.1)'}`,
                background: 'rgba(255,255,255,0.03)',
                transition: 'border-color 0.2s',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer text-white/30 hover:text-white/60 transition-colors"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* Tag filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveTag(null)}
            className="whitespace-nowrap px-3.5 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
            style={
              activeTag === null
                ? { background: color, color: '#050508', boxShadow: `0 0 10px ${color}50` }
                : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.08)' }
            }
          >
            Все вопросы
          </button>
          {allTags.map(tag => {
            const tagItem = FAQ_ITEMS.find(f => f.tag === tag);
            const tc = tagItem?.tagColor ?? color;
            const isActive = activeTag === tag;
            return (
              <button
                key={tag}
                onClick={() => setActiveTag(isActive ? null : tag)}
                className="whitespace-nowrap px-3.5 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
                style={
                  isActive
                    ? { background: tc, color: '#050508', boxShadow: `0 0 10px ${tc}50` }
                    : { background: `${tc}10`, color: tc, border: `1px solid ${tc}30` }
                }
              >
                {tag}
              </button>
            );
          })}
        </div>

        {/* Accordion list */}
        {filtered.length > 0 ? (
          <div className="space-y-3">
            {filtered.map((item, i) => (
              <FaqAccordionItem
                key={item.id}
                item={item}
                isOpen={openId === item.id}
                onToggle={() => handleToggle(item.id)}
                index={FAQ_ITEMS.indexOf(item)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div
              className="w-16 h-16 flex items-center justify-center rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <i className="ri-question-line text-3xl text-white/20" />
            </div>
            <p className="font-mono text-sm text-white/35">Ничего не найдено по запросу «{searchQuery}»</p>
            <button
              onClick={() => { setSearchQuery(''); setActiveTag(null); }}
              className="whitespace-nowrap font-mono text-xs cursor-pointer transition-colors"
              style={{ color: `${color}cc` }}
            >
              Сбросить фильтры
            </button>
          </div>
        )}

        {/* Bottom CTA */}
        <div
          className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-2xl"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
              style={{ background: `${color}12`, border: `1px solid ${color}30` }}
            >
              <i className="ri-customer-service-2-line text-xl" style={{ color }} />
            </div>
            <div>
              <p className="font-orbitron font-bold text-sm text-white">Не нашли ответ?</p>
              <p className="font-mono text-xs text-white/40 mt-0.5">Наши фармацевты ответят на любой вопрос</p>
            </div>
          </div>
          <div className="flex gap-3 shrink-0">
            <a
              href="tel:+78001234567"
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300"
              style={{ border: `1px solid ${color}40`, color }}
            >
              <i className="ri-phone-line" />
              8 800 123-45-67
            </a>
            <a
              href="#"
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300"
              style={{ background: color, color: '#050508', boxShadow: `0 0 15px ${color}50` }}
            >
              <i className="ri-chat-3-line" />
              Онлайн-чат
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ProductFaq;
